import { List, Stack, Typography } from '@mui/material'
import { type PropsWithChildren } from 'react'

import { CodeBlock } from 'components/common/CodeBlock'
import { AuthenticationStep } from 'components/common/mapped-entities/AuthenticationStep'
import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { type GcpIntegrationSetupResponse } from 'types/api/gcp/integrations'

function CodeList({ list }: { list: string[] }) {
  return <CodeBlock code={list.join('\n')} includeLineNumbers={false} />
}

const AUTHENTICATION_STEP_EXTERNAL_LINK_ICON_STYLES = {
  height: muiTheme.spacing(2),
  marginLeft: muiTheme.spacing(0.5),
  width: muiTheme.spacing(2)
}

/**
 * children will likely be a form context wrapping the necessary auth fields in a form controller
 */
export function GcpAuthenticationSection({
  children,
  gremlinGcpProxyServiceAccount,
  principalAccessRoles,
  serviceAccountRolePrivileges
}: PropsWithChildren<GcpIntegrationSetupResponse>) {
  return (
    <Stack sx={{ rowGap: 2, width: '100%' }}>
      <Typography sx={{ marginTop: 2 }}>
        In the GCP Console, navigate to your preferred project before following
        the next steps.
      </Typography>
      <List disablePadding>
        <AuthenticationStep
          badgeNumber={1}
          title={
            <Typography variant="inherit">
              Under{' '}
              <MUIExternalLink href="https://console.cloud.google.com/iam-admin/roles">
                Roles
                <Icon
                  css={AUTHENTICATION_STEP_EXTERNAL_LINK_ICON_STYLES}
                  icon="arrowUpRightFromSquare"
                />
              </MUIExternalLink>
              , create a custom role
            </Typography>
          }
        >
          <Typography>
            Set the "Role launch stage" to "General Availability" and assign the
            following permissions:
          </Typography>
          <CodeList list={serviceAccountRolePrivileges} />
        </AuthenticationStep>
        <AuthenticationStep
          badgeNumber={2}
          title={
            <Typography variant="inherit">
              Under{' '}
              <MUIExternalLink href="https://console.cloud.google.com/iam-admin/serviceaccounts">
                Service Accounts
                <Icon
                  css={AUTHENTICATION_STEP_EXTERNAL_LINK_ICON_STYLES}
                  icon="arrowUpRightFromSquare"
                />
              </MUIExternalLink>
              , create a new service account
            </Typography>
          }
        >
          <Typography>
            We recommended calling it “gremlin-access”. Grant the{' '}
            <code>{gremlinGcpProxyServiceAccount}</code> principal access with
            the following roles:
          </Typography>
          <CodeList list={principalAccessRoles} />
        </AuthenticationStep>
        <AuthenticationStep
          badgeNumber={3}
          title={
            <Typography variant="inherit">
              Under{' '}
              <MUIExternalLink href="https://console.cloud.google.com/iam-admin/iam">
                IAM
                <Icon
                  css={AUTHENTICATION_STEP_EXTERNAL_LINK_ICON_STYLES}
                  icon="arrowUpRightFromSquare"
                />
              </MUIExternalLink>
              , configure project access
            </Typography>
          }
        >
          <Typography>
            Grant access for the gremlin-access service account, assigning it
            the new custom role.
          </Typography>
        </AuthenticationStep>
        <AuthenticationStep
          badgeNumber={4}
          title="Paste gremlin-access service account's email"
        >
          {children}
        </AuthenticationStep>
      </List>
    </Stack>
  )
}
